import React from 'react'
import { makeStyles } from '@material-ui/core/styles'

import Image from 'next/image'
import { useRouter } from 'next/router'
import {
  LinearProgress,
  Typography,
  withStyles,
  Grid,
  Accordion,
  AccordionSummary,
  Hidden,
  Link,
  Button,
  AccordionDetails,
} from '@material-ui/core'
import ArrowDropDownRoundedIcon from '@material-ui/icons/ArrowDropDownRounded'
import { urls } from '../../../../utils/config'
import Attribute from '../../../AttributesList/Attribute'

// types
import { Exchange as ExchangeType } from '../../../../../types/exchange'
import { Cryptocurrency, Fiat } from '../../../../../types/currency'

// utils
import { calculateAverageRating } from '../../../../utils/wallet'

const BorderLinearProgress = withStyles((theme) => ({
  root: {
    height: 6,
    borderRadius: 5,
  },
  colorPrimary: {
    backgroundColor: theme.palette.grey[300],
  },
  bar: {
    borderRadius: 5,
    backgroundColor: theme.palette.primary.main,
  },
}))(LinearProgress)

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: '12px',
    padding: '0',
    alignItems: 'center',
    border: 0,
    [theme.breakpoints.up('md')]: {
      padding: '1rem 0',
    },
  },
  imageWrapper: {
    padding: '.4rem',
    background: 'white',
    borderRadius: '.6rem',
    width: '2.8rem',
    height: '2.8rem',
    margin: '.8rem 0 .4rem 0',
    [theme.breakpoints.up('md')]: {
      margin: '0 .4rem',
    },
  },
  imageContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    position: 'relative',
    overflow: 'hidden',
  },
  detailsContainer: {
    width: '100%',
    textAlign: 'center',
    '& > p': {
      margin: '.4rem 0',
      fontSize: '.8rem',
      [theme.breakpoints.up('md')]: {},
    },
    [theme.breakpoints.up('md')]: {
      textAlign: 'left',
      width: '60%',
    },
  },
  accordionSummary: {
    padding: '1rem 2rem',
    width: '100%',
    borderRadius: '1rem',
    border: '1px solid rgba(0, 0, 0, 0) !important',
    '&.expanded': {
      border: '1px solid rgba(0, 0, 0, 0.05) !important',
      '& $accordionSummaryContent': {
        margin: '0 !important',
      },
    },
    '& p': {
      fontSize: '1rem',
      fontWeight: '600',
      color: 'rgba(43, 24, 65, .8)',
    },
    '&.odd': {
      background: 'rgba(0, 0, 0, 0.04)',
    },
  },
  accordionSummaryContent: {
    margin: 0,
  },
  securityScore: {
    textAlign: 'center',
  },
  price: {},
  feesContainer: {},
  starting: {},
  dropDownIconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& > svg': {
      transform: 'rotateZ(0deg)',
      transition: 'transform 0.2s ease-in-out',
    },
    '&.expanded > svg': {
      transform: 'rotateZ(-180deg)',
      transition: 'transform 0.2s ease-in-out',
    },
  },
  number: {
    [theme.breakpoints.up('md')]: {
      // margin: '0 1rem',
    },
  },
  title: {
    textOverflow: 'ellipsis',
  },
  numberContainer: {},
  imageGrid: {
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  accordionDetailsContainer: {
    marginTop: '1rem',
    padding: '4rem 2rem',
  },
  accordionDetailsContent: {
    flexDirection: 'column',
    alignItems: 'center',
    '& button': {
      margin: '1rem 1rem',
    },
    '& button p': {
      color: 'white',
      fontSize: '.8rem',
    },
    '& a': {
      color: 'black',
      fontSize: '.8rem',
      borderBottom: '1px solid',
      paddingBottom: '.4rem',
      margin: '1rem 1rem',
      width: 'fit-content',
    },
  },
  descriptionContainer: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '50%',
    },
    '& p': {
      fontWeight: '400',
      textAlign: 'center',
    },
  },
  ctaContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
}))

interface props {
  exchange: ExchangeType
  cryptocurrency?: Cryptocurrency
  fiat?: Fiat
  number?: number
}

function ExchangeCard({ exchange, number }: props): JSX.Element {
  const classes = useStyles({})

  const [expanded, setExpanded] = React.useState(false)

  const handleChange = () => {
    setExpanded(!expanded)
  }

  const { push } = useRouter()
  const goToSite = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    window.open(exchange.url)
  }

  const averageRating = calculateAverageRating(exchange)

  return (
    <Accordion className={classes.root} onChange={handleChange}>
      <AccordionSummary
        classes={{
          root: `
          ${classes.accordionSummary} 
          ${number && number % 2 === 0 ? 'even' : 'odd'} 
          ${expanded ? 'expanded' : ''}`,
          content: classes.accordionSummaryContent,
        }}
      >
        <Grid container alignItems="center" spacing={6}>
          <Grid item container alignItems="center" xs={3}>
            <Grid
              container
              item
              xs={12}
              md={3}
              alignItems="center"
              className={classes.numberContainer}
              justify="flex-start"
            >
              <div className={`${classes.dropDownIconContainer} ${expanded ? 'expanded' : ''}`}>
                <ArrowDropDownRoundedIcon />
              </div>
              <Typography className={classes.number} variant="body2">
                {number && `${number}.`}
              </Typography>
            </Grid>
            <Grid
              item
              container
              xs={12}
              md={9}
              alignItems="center"
              justify="flex-start"
              className={classes.imageGrid}
            >
              <div className={classes.imageWrapper}>
                <div className={classes.imageContainer}>
                  <Image
                    src={exchange.icon ? exchange.icon.url : '/svg/wallet.svg'}
                    objectFit="contain"
                    layout="fill"
                  />
                </div>
              </div>
              <Typography className={classes.title} variant="body2">
                {exchange.title}
              </Typography>
            </Grid>
          </Grid>
          <Grid item container justify="center" alignItems="center" xs={3} spacing={1}>
            <Grid item xs={12} md={10}>
              <BorderLinearProgress
                variant="determinate"
                value={(Number.parseFloat(averageRating.toFixed(1)) / 5) * 100}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography className={classes.securityScore} variant="body2">
                {exchange.cash_trades ? 'Yes' : 'No'}
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={2}>
            <Typography className={classes.price} variant="body2">
              {averageRating.toFixed(1)}
            </Typography>
          </Grid>
          <Grid item xs={1}>
            <Typography className={classes.starting} variant="body2">
              {exchange.kyc_required ? 'Yes' : 'No'}
            </Typography>
          </Grid>
          <Hidden smDown>
            <Grid container alignItems="center" item xs={3} className={classes.feesContainer}>
              {exchange.cryptocurrencies.slice(0, 5).map((currency) => (
                <Attribute {...currency} key={currency.code} />
              ))}
              <Typography variant="body2">{''}</Typography>
            </Grid>
          </Hidden>
        </Grid>
      </AccordionSummary>
      <AccordionDetails
        classes={{
          root: `
          ${classes.accordionSummary} 
          ${number && number % 2 === 0 ? 'even' : 'odd'} 
          ${expanded ? 'expanded' : ''} 
          ${classes.accordionDetailsContainer}
          `,
        }}
      >
        <Grid container spacing={2} justify="center" className={classes.accordionDetailsContent}>
          <div className={classes.descriptionContainer}>
            <Typography variant="body2">{exchange.seo_description}</Typography>
          </div>
          <div className={classes.ctaContainer}>
            <Button onClick={goToSite} variant="contained" color="primary">
              <Typography variant="body2">{`Try ${exchange.title} now!`}</Typography>
            </Button>
            <Link
              underline="none"
              href={`${urls.p2p}/${exchange.path_id}`}
            >{`Learn more about ${exchange.title}`}</Link>
          </div>
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}

export default ExchangeCard
